import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Warning, Star } from "@phosphor-icons/react";
import Brand from "@/components/Brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PublicInterviewAssessment — V1.5.7 magic-link form for interviewers.
 * Route: /interview-assessment/:token. No auth required. Token single-use.
 */
export default function PublicInterviewAssessment() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [tpl, setTpl] = useState(null);
    const [skillRatings, setSkillRatings] = useState({});   // {skill: 1-5}
    const [generic, setGeneric] = useState({});             // {key: 1-5}
    const [notes, setNotes] = useState("");
    const [status, setStatus] = useState("");               // "selected" | "rejected"
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/public/interview-assessment/${token}`);
            setTpl(r.data);
        } catch (ex) {
            setError(ex.response?.data?.detail || "This link is invalid or has expired.");
        } finally {
            setLoading(false);
        }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    const canSubmit = useMemo(() => {
        if (!status) return false;
        // Require every generic question to be rated. Skill ratings optional.
        return (tpl?.generic_questions || []).every((q) => generic[q.key]);
    }, [status, generic, tpl]);

    const submit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setError("");
        try {
            await axios.post(`${API}/public/interview-assessment/${token}`, {
                skill_ratings: Object.entries(skillRatings).map(([skill, rating]) => ({ skill, rating })),
                generic,
                status,
                notes,
            });
            setSubmitted(true);
        } catch (ex) {
            setError(ex.response?.data?.detail || "Submission failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen bg-paper flex items-center justify-center p-6 text-sm text-[#525252]">Loading form…</div>;
    }
    if (error && !tpl) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center p-6">
                <div className="max-w-md w-full border-2 border-[#DC2626] bg-white p-6 text-center">
                    <Warning size={32} weight="fill" className="text-[#DC2626] mx-auto mb-3" />
                    <p className="text-lg font-black uppercase tracking-wider text-ink mb-2">Link unavailable</p>
                    <p className="text-sm text-[#525252]">{error}</p>
                </div>
            </div>
        );
    }
    if (submitted) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center p-6">
                <div className="max-w-md w-full border-2 border-emerald-500 bg-white p-6 text-center">
                    <CheckCircle size={48} weight="fill" className="text-emerald-500 mx-auto mb-3" />
                    <p className="text-xl font-black uppercase tracking-wider text-ink mb-2">Thank you!</p>
                    <p className="text-sm text-[#525252]">Your assessment has been submitted.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-paper p-4 sm:p-6" data-testid="public-assessment-page">
            <div className="max-w-xl mx-auto">
                <div className="flex items-center justify-center mb-5 pt-2">
                    <Brand
                        variant="dark" size="h-9"
                        logoUrl={tpl?.workspace?.logo_url || null}
                        name={tpl?.workspace?.name || "NaukriDaddy"}
                        to={null}
                    />
                </div>
                <div className="mb-5">
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-cobalt">
                        {tpl?.workspace?.name || "NaukriDaddy"} · Interview Assessment
                    </p>
                    <h1 className="text-2xl sm:text-3xl font-black text-ink mt-1">Rate {tpl?.candidate?.name?.split(" ")[0] || "candidate"}</h1>
                    <p className="text-sm text-[#525252] mt-2">
                        Role: <strong className="text-ink">{tpl?.job?.title || "—"}</strong>
                        {tpl?.interview?.start && (
                            <> · Interview on <strong className="text-ink">{new Date(tpl.interview.start).toLocaleString()}</strong></>
                        )}
                    </p>
                </div>
                <div className="bg-white border-2 border-ink p-4 sm:p-6 space-y-5"
                     data-testid="public-assessment-form">
                    {/* Skills */}
                    {(tpl?.skills || []).length > 0 && (
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-2">
                                Job-specific skills
                            </p>
                            <div className="space-y-2">
                                {(tpl.skills || []).map((s) => (
                                    <StarRow
                                        key={s}
                                        label={s}
                                        value={skillRatings[s] || 0}
                                        onChange={(v) => setSkillRatings((prev) => ({ ...prev, [s]: v }))}
                                        testId={`assessment-skill-${s}`}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {/* Generic evaluation */}
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-2">
                            Overall evaluation <span className="text-[#DC2626]">*</span>
                        </p>
                        <div className="space-y-2">
                            {(tpl?.generic_questions || []).map((q) => (
                                <StarRow
                                    key={q.key}
                                    label={q.label}
                                    value={generic[q.key] || 0}
                                    onChange={(v) => setGeneric((prev) => ({ ...prev, [q.key]: v }))}
                                    testId={`assessment-generic-${q.key}`}
                                />
                            ))}
                        </div>
                    </div>
                    {/* Notes */}
                    <div>
                        <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5">
                            Notes (optional)
                        </label>
                        <textarea
                            data-testid="assessment-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            placeholder="Anything else the recruiter should know…"
                            className="w-full bg-white border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                        />
                    </div>
                    {/* Status */}
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-2">
                            Verdict <span className="text-[#DC2626]">*</span>
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                data-testid="assessment-status-selected"
                                onClick={() => setStatus("selected")}
                                className={`py-3 text-sm font-black uppercase tracking-wider border-2 transition-colors ${
                                    status === "selected"
                                        ? "bg-emerald-600 border-emerald-600 text-white"
                                        : "bg-white border-[#E5E5E5] text-[#525252] hover:border-emerald-600 hover:text-emerald-700"
                                }`}
                            >
                                ✓ Selected
                            </button>
                            <button
                                type="button"
                                data-testid="assessment-status-rejected"
                                onClick={() => setStatus("rejected")}
                                className={`py-3 text-sm font-black uppercase tracking-wider border-2 transition-colors ${
                                    status === "rejected"
                                        ? "bg-[#DC2626] border-[#DC2626] text-white"
                                        : "bg-white border-[#E5E5E5] text-[#525252] hover:border-[#DC2626] hover:text-[#DC2626]"
                                }`}
                            >
                                ✗ Rejected
                            </button>
                        </div>
                    </div>
                    {error && (
                        <div className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-xs px-3 py-2">
                            {error}
                        </div>
                    )}
                    <button
                        data-testid="public-assessment-submit"
                        onClick={submit}
                        disabled={submitting || !canSubmit}
                        className="w-full bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 disabled:cursor-not-allowed text-white py-3 text-sm font-black tracking-wider uppercase transition-colors"
                    >
                        {submitting ? "Submitting…" : "Submit assessment"}
                    </button>
                </div>
                <p className="text-[10px] text-[#A3A3A3] mt-4 text-center">
                    Link expires after submission. Your rating is shared only with the recruiter.
                </p>
            </div>
        </div>
    );
}

function StarRow({ label, value, onChange, testId }) {
    return (
        <div data-testid={testId} className="flex items-center justify-between gap-3 p-2 border border-[#E5E5E5]">
            <p className="text-sm font-bold text-ink flex-1 min-w-0 break-words">{label}</p>
            <div className="flex items-center gap-1 shrink-0">
                {[1, 2, 3, 4, 5].map((n) => (
                    <button
                        key={n}
                        type="button"
                        data-testid={`${testId}-${n}`}
                        onClick={() => onChange(n)}
                        className={n <= value ? "text-[#F59E0B]" : "text-[#E5E5E5] hover:text-[#A3A3A3]"}
                        aria-label={`Rate ${n}`}
                    >
                        <Star size={18} weight="fill" />
                    </button>
                ))}
                {value > 0 && <span className="text-[10px] font-bold text-[#525252] ml-1">{value}/5</span>}
            </div>
        </div>
    );
}
